<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Mouvements Ingrédients</h1>
        <p class="page-subtitle">{{ rows.length }} mouvement{{ rows.length > 1 ? 's' : '' }}</p>
      </div>
      <div>
        <v-btn color="primary" depressed small @click="openCreate"><v-icon left small>mdi-plus</v-icon>Nouveau</v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <div class="d-flex justify-end mb-3" style="gap: 8px;">
        <v-select
          v-model="filterStore"
          :items="stores"
          item-text="name"
          item-value="id"
          outlined dense hide-details clearable
          placeholder="Tous les emplacements"
          prepend-inner-icon="mdi-store-outline"
          style="max-width: 240px;"
          @change="reloadList"
        ></v-select>
        <input v-model="search" type="text" placeholder="Rechercher..." class="search-input" />
      </div>

      <v-card flat class="table-card">
        <v-data-table
          :headers="headers"
          :items="filteredRows"
          :items-per-page="15"
          no-data-text="Aucun mouvement pour cette période"
          :footer-props="{ 'items-per-page-text': 'Par page', 'items-per-page-options': [10, 15, 25, 50, -1] }"
        >
          <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
          <template v-slot:item.ingredient.name="{ item }">
            <span class="font-weight-medium">{{ item.ingredient && item.ingredient.name }}</span>
          </template>
          <template v-slot:item.type="{ item }">
            <v-chip x-small :color="typeColor(item.type)" dark>{{ typeLabel(item.type) }}</v-chip>
          </template>
          <template v-slot:item.quantity="{ item }">
            <span :class="parseFloat(item.quantity) < 0 ? 'error--text font-weight-bold' : ''">{{ fmt(item.quantity) }}</span>
          </template>
          <template v-slot:item.cost="{ item }">{{ fmt(item.cost) }}</template>
          <template v-slot:item.memo="{ item }">
            <span class="text-caption text--secondary">{{ item.memo }}</span>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="dialog" max-width="480" @click:outside="close">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-package-variant</v-icon>
          Nouveau mouvement
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <div class="form-group">
            <label class="form-label">Ingrédient *</label>
            <v-select v-model="op.ingredient_id" :items="ingredients" item-text="name" item-value="id" outlined dense hide-details></v-select>
          </div>
          <div class="form-group">
            <label class="form-label">Emplacement *</label>
            <v-select v-model="op.store_id" :items="stores" item-text="name" item-value="id" outlined dense hide-details></v-select>
          </div>
          <div class="form-group">
            <label class="form-label">Type *</label>
            <v-select v-model="op.type" :items="typeOptions" item-text="label" item-value="value" outlined dense hide-details></v-select>
          </div>
          <v-row>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Quantité *</label>
                <v-text-field v-model.number="op.quantity" type="number" min="0" step="0.01" outlined dense hide-details></v-text-field>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Coût total</label>
                <v-text-field v-model.number="op.cost" type="number" min="0" outlined dense hide-details></v-text-field>
              </div>
            </v-col>
          </v-row>
          <div class="form-group">
            <label class="form-label">Memo</label>
            <v-text-field v-model="op.memo" outlined dense hide-details></v-text-field>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="saving" :disabled="saving || !op.ingredient_id || !op.store_id || !op.type || op.quantity <= 0" @click="save">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

// Les quantités/coûts arrivent en chaînes de l'API : tri numérique explicite
function numSort(a, b) { return (parseFloat(a) || 0) - (parseFloat(b) || 0) }

export default {
  name: 'IngredientStockMovementView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Mouvements Ingrédients', disabled: true, href: '/ingredient-movements' }],
    enable: false,
    saving: false,
    dialog: false,
    search: '',
    filterStore: null,
    rows: [],
    ingredients: [],
    stores: [],
    headers: [
      { text: 'DATE', value: 'created_at' },
      { text: 'INGRÉDIENT', value: 'ingredient.name' },
      { text: 'TYPE', value: 'type' },
      { text: 'QUANTITÉ', value: 'quantity', align: 'end', sort: numSort },
      { text: 'COÛT', value: 'cost', align: 'end', sort: numSort },
      { text: 'MEMO', value: 'memo', sortable: false },
    ],
    typeOptions: [
      { label: 'Réception (+)', value: 1 },
      { label: 'Sortie (−)', value: 2 },
      { label: 'Ajustement (+)', value: 3 },
    ],
    op: { ingredient_id: null, store_id: null, type: 1, quantity: 0, cost: 0, memo: '' },
  }),
  computed: {
    filteredRows() {
      if (!this.search.trim()) return this.rows
      var q = this.search.toLowerCase()
      return this.rows.filter(function (r) { return r.ingredient && r.ingredient.name && r.ingredient.name.toLowerCase().indexOf(q) !== -1 })
    },
  },
  methods: {
    fmt(v) { var n = parseFloat(v); if (!n && n !== 0) return '0'; return n.toFixed(2) },
    formatDate(d) { if (!d) return ''; return String(d).substring(0, 19).replace('T', ' ') },
    typeLabel(t) { return { 1: 'Réception', 2: 'Sortie', 3: 'Ajustement' }[t] || '?' },
    typeColor(t) { return { 1: 'success', 2: 'error', 3: 'warning' }[t] || 'grey' },
    getDateParams() {
      var isValid = function (v) { return v && v !== 'null' && v.trim() !== '' }
      return {
        from: isValid(localStorage.debut_periode) ? localStorage.debut_periode : '',
        to: isValid(localStorage.fin_periode) ? localStorage.fin_periode : ''
      }
    },
    movementsUrl() {
      var url = '/ingredient-operations?include=ingredient&orderdesc=id'
      // Le listing générique applique where(store_id) quand le paramètre est fourni,
      // et from/to (whereDate sur created_at) pour la période globale du dashboard
      if (this.filterStore) url += '&store_id=' + this.filterStore
      var d = this.getDateParams()
      if (d.from) url += '&from=' + d.from
      if (d.to) url += '&to=' + d.to
      return url
    },
    async load() {
      var self = this
      try {
        var [movements, ingredients, stores] = await Promise.all([
          this.$axios.get(this.movementsUrl()),
          this.$axios.get('/ingredients?orderasc=name'),
          this.$axios.get('/stores?orderasc=name&business_id=' + (JSON.parse(localStorage.business || '{}').id || 0)),
        ])
        self.rows = movements.data || []
        self.ingredients = ingredients.data || []
        self.stores = stores.data || []
      } catch (e) { /* ignore */ }
      self.enable = true
    },
    async reloadList() {
      var self = this
      try {
        var movements = await this.$axios.get(this.movementsUrl())
        self.rows = movements.data || []
      } catch (e) { /* ignore */ }
    },
    openCreate() { this.reset(); this.dialog = true },
    save() {
      var self = this; this.saving = true
      this.$axios.post('/ingredient-operations', this.op)
        .then(function (r) { self.rows.unshift(r.data); self.dialog = false; self.saving = false; self.reset() })
        .catch(function () { self.saving = false })
    },
    close() { this.dialog = false; this.reset(); this.saving = false },
    reset() { this.op = { ingredient_id: null, store_id: this.stores[0] ? this.stores[0].id : null, type: 1, quantity: 0, cost: 0, memo: '' } },
  },
  mounted() { this.load() },
}
</script>

<style scoped>
.search-input { width: 260px; padding: 8px 12px; font-size: 0.8125rem; border: 1px solid #cbd5e1; border-radius: 6px; outline: none; }
.search-input:focus { border-color: #194173; box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1); }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; overflow: hidden; }
.table-card >>> .v-data-table thead th { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8 !important; padding: 8px 16px !important; }
.table-card >>> .v-data-table tbody td { font-size: 0.8125rem; padding: 10px 16px !important; }
.dialog-card { border-radius: 12px !important; }
.dialog-title { font-size: 0.9375rem !important; font-weight: 600 !important; padding: 14px 20px 12px !important; }
.dialog-body { padding: 20px 20px 8px !important; }
.dialog-actions { padding: 8px 16px 12px !important; }
.form-group { margin-bottom: 16px; }
.form-label { display: block; font-size: 0.75rem; font-weight: 600; color: #475569; margin-bottom: 4px; }
</style>
